import { getProgramByCodeFn } from "@/actions/program/queries.functions";
import { joinProgramByCodeFn } from "@/actions/user-program/mutations";
import { formatDaysPerWeekShort } from "@/api/db/schema/training-programs";
import {
  trackProgramJoinAttempt,
  trackProgramJoinError,
  trackProgramStart,
} from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Calendar, Layers, Trophy, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
export const Route = createFileRoute("/_app/join/$code")({
  loader: async ({ params }) => {
    const result = await getProgramByCodeFn({ data: { code: params.code } });

    if (result.status !== 200) {
      return { error: result.body.message, program: null };
    }

    return { program: result.body, error: null };
  },
  component: JoinComponent,
});

function JoinComponent() {
  const { program, error } = Route.useLoaderData();
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const { user } = Route.useRouteContext();
  const [joinError, setJoinError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => joinProgramByCodeFn({ data: { code } }),
    onSuccess: (result) => {
      if (result.body.success) {
        trackProgramStart(program!.programId, program!.name);
        navigate({
          to: "/program/$programId",
          params: { programId: program!.programId },
        });
      } else {
        trackProgramJoinError();
        setJoinError(result.body.error || "Bir hata oluştu");
      }
    },
    onError: () => {
      trackProgramJoinError();
      setJoinError("Bağlantı hatası oluştu");
    },
  });

  const handleJoin = () => {
    if (!user) {
      toast.info("Programa katılmak için lütfen giriş yapın.");
      navigate({
        to: "/password/login",
        search: { redirect: `/join/${code}` },
      });
      return;
    }
    setJoinError(null);
    trackProgramJoinAttempt("invite_page");
    mutation.mutate();
  };

  if (error || !program) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center p-4 text-center">
        <h1 className="mb-4 font-bold text-3xl text-destructive uppercase tracking-tighter">
          Hata
        </h1>
        <div className="max-w-md rounded-2xl border border-destructive/20 bg-destructive/5 p-8 shadow-sm backdrop-blur-md">
          <p className="font-medium text-destructive">
            {error || "Program bulunamadı"}
          </p>
          <Button
            variant="ghost"
            className="mt-6 transition-colors hover:bg-destructive/10 hover:text-destructive"
            onClick={() => navigate({ to: "/" })}
          >
            Anasayfaya Dön
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 pt-20">
      <Card className="w-full max-w-md overflow-hidden border border-border/40 bg-card">
        {program.image && (
          <div className="flex w-full items-center justify-center overflow-hidden border-b bg-muted/20 p-2">
            <img
              src={program.image}
              alt={program.name}
              className="h-auto max-h-48 w-full object-contain"
            />
          </div>
        )}
        <CardHeader className="pt-8 pb-4 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 rotate-3 items-center justify-center rounded-2xl bg-muted transition-transform hover:rotate-0">
            <Trophy className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="font-extrabold text-3xl tracking-tight">
            Programa Davet
          </CardTitle>
          <CardDescription className="mt-2 text-base">
            Yeni bir antrenman macerasına başlamak üzeresiniz!
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-8 py-6">
          <div className="rounded-2xl border border-border/40 bg-muted/30 p-6">
            <h3 className="mb-4 text-center font-black text-2xl text-foreground uppercase tracking-tighter">
              {program.name}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-background/50 p-3">
                <Calendar className="mb-1.5 h-5 w-5 text-primary" />
                <span className="font-bold text-sm tracking-tight">
                  {program.durationWeeks} Hafta
                </span>
                <span className="font-semibold text-[10px] text-muted-foreground uppercase">
                  Süre
                </span>
              </div>
              <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-background/50 p-3">
                <Layers className="mb-1.5 h-5 w-5 text-primary" />
                <span className="font-bold text-sm tracking-tight">
                  {formatDaysPerWeekShort(
                    program.daysPerWeek,
                    program.maxDaysPerWeek,
                  )}
                </span>
                <span className="font-semibold text-[10px] text-muted-foreground uppercase">
                  Sıklık
                </span>
              </div>
            </div>

            {program.description && (
              <p className="mt-6 line-clamp-3 text-center text-muted-foreground text-sm italic leading-relaxed">
                "{program.description}"
              </p>
            )}
          </div>

          {user ? (
            <div className="space-y-3 px-4 text-center text-muted-foreground text-xs leading-relaxed">
              <p>
                Bu programa katıldığınızda, mevcut aktif programınız{" "}
                <span className="font-semibold text-foreground">
                  duraklatılacak
                </span>{" "}
                ve takviminiz güncellenecektir.
              </p>
              <p className="text-[11px] text-muted-foreground/80">
                Programa katılarak{" "}
                <Link
                  to="/privacy"
                  search={{ tab: "kvkk" }}
                  target="_blank"
                  className="text-foreground underline underline-offset-2 hover:text-primary"
                >
                  KVKK Aydınlatma Metni
                </Link>
                ,{" "}
                <Link
                  to="/privacy"
                  search={{ tab: "videos" }}
                  target="_blank"
                  className="text-foreground underline underline-offset-2 hover:text-primary"
                >
                  Video Açık Rızası
                </Link>{" "}
                ve{" "}
                <Link
                  to="/privacy"
                  search={{ tab: "terms" }}
                  target="_blank"
                  className="text-foreground underline underline-offset-2 hover:text-primary"
                >
                  Kullanım Şartları
                </Link>
                'nı kabul etmiş sayılırsınız.
              </p>
            </div>
          ) : (
            <div className="px-4 text-center text-muted-foreground text-sm leading-relaxed">
              Programa katılmak için önce giriş yapmanız veya hesap oluşturmanız
              gerekmektedir.
            </div>
          )}

          {joinError && (
            <div className="fade-in zoom-in animate-in duration-300">
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
                    <XCircle className="h-4 w-4 text-destructive" />
                  </div>
                  <p className="font-semibold text-destructive text-sm">
                    Katılım Sağlanamadı
                  </p>
                </div>
                <p className="pl-10 text-destructive/80 text-sm leading-relaxed">
                  {joinError}
                </p>
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col gap-3 px-8 pb-8">
          <Button
            className="w-full py-6 font-bold text-lg shadow-primary/20 transition-all hover:scale-[1.02]"
            onClick={handleJoin}
            disabled={user ? mutation.isPending : false}
          >
            {user
              ? mutation.isPending
                ? "Katılım Yapılıyor..."
                : "Programa Katıl ve Başla"
              : "Giriş Yap ve Programa Katıl"}
          </Button>
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => navigate({ to: "/" })}
            disabled={mutation.isPending}
          >
            Vazgeç
          </Button>
        </CardFooter>
      </Card>

      <p className="mt-8 max-w-xs text-center text-muted-foreground text-xs">
        Bu program bir erişim kodu ile paylaşılmıştır. Sadece bu koda sahip
        kullanıcılar katılabilir.
      </p>
    </div>
  );
}
